import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IStudy } from "../../../@types/admin/AdminStudy";
import { TableSorted } from "../../../components/TableSorted";
import { useStudies } from "../../../hooks/queries/admin/studies";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FilterButton,
  FiltersContainer,
  FiltersActions,
  PageContainer,
  PageHeader,
  PageTitle,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { SignupCodeCell } from "./SignupCodeCell";
import { formatStudyDuration, parseCmsBoolean } from "./studyFields";
import { StudyModal } from "./StudyModal";

const SEARCH_FIELDS: (keyof IStudy)[] = [
  "id",
  "name",
  "code",
  "hospitalName",
  "conditionTypeName",
];

/**
 * Studies (signup codes) admin page (EN-5155), migrated from the Retool
 * Studies app. Lists the studies and creates or edits one — a study is a
 * signup code that routes an enrolling patient to a ward and condition.
 *
 * Retool opened the edit form on a double-click and kept the row selected
 * afterwards, so the next "Add new study" click had to clear the selection
 * first or it silently edited instead. The portal passes the row to the modal
 * explicitly, which removes that mode entirely.
 */
export function AdminStudiesList() {
  document.title = "Studies - Admin";

  const { openModal, closeModal } = useModal();
  const { studies, isLoading, isError } = useStudies();

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: studies, searchFields: SEARCH_FIELDS });

  const openStudyModal = (study?: IStudy) => {
    openModal(<StudyModal study={study} onClose={closeModal} />, {
      width: "520px",
      showCloseButton: true,
    });
  };

  const columns: ColumnDef<IStudy, string>[] = useMemo(
    () => [
      { accessorKey: "id", header: "ID", enableSorting: true },
      { accessorKey: "name", header: "Name", enableSorting: true },
      {
        accessorKey: "code",
        header: "Signup code",
        cell: ({ row }) => <SignupCodeCell code={row.original.code} />,
        enableSorting: true,
      },
      {
        id: "duration",
        header: "Duration",
        accessorFn: (study) => formatStudyDuration(study.duration),
        enableSorting: true,
      },
      {
        accessorKey: "expireAt",
        header: "Expires at",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.expireAt, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "hospitalName",
        header: "Ward",
        enableSorting: true,
      },
      {
        accessorKey: "conditionTypeName",
        header: "Condition type",
        enableSorting: true,
      },
      {
        // `publish` arrives as the string "True"/"False"; the accessor feeds
        // both the badge and the sort, since TableSorted dashes a falsy value.
        id: "publish",
        header: "Publish",
        accessorFn: (study) =>
          parseCmsBoolean(study.publish) ? "Published" : "Unpublished",
        cell: ({ row }) => (
          <StatusBadge
            status={
              parseCmsBoolean(row.original.publish) ? "active" : "inactive"
            }
          >
            {parseCmsBoolean(row.original.publish)
              ? "Published"
              : "Unpublished"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        // `isValid` is created + duration vs now — a published study whose
        // window has closed still takes no signups, and Retool hid this column.
        id: "isValid",
        header: "Status",
        accessorFn: (study) => (study.isValid ? "Active" : "Expired"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.isValid ? "active" : "inactive"}>
            {row.original.isValid ? "Active" : "Expired"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Studies</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by name, signup code, ward, or condition type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FiltersActions>
          <FilterButton variant="primary" onClick={() => openStudyModal()}>
            Add study
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the studies. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-studies"]]}>
          <TableSorted
            columns={columns}
            data={items}
            onRowClick={(study: IStudy) => openStudyModal(study)}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "studies",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
